// Utility to mask phone number, e.g. +639171234567 => +63*****67
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  signInWithEmailAndPassword,
} from "firebase/auth";
import styles from "./login.module.scss";
import { Path } from "enums";
import env from "environments/env";
import { redirectTo } from "utils";

const MfaStep = {
  Challenge: "challenge",
  OtpVerification: "otpVerification",
};

function maskPhoneNumber(phone) {
  if (!phone) return "";
  // Remove non-digits
  let digits = phone.replace(/\D/g, "");
  // Convert 09... to 9... for local, then add +63
  if (digits.startsWith("0")) digits = digits.slice(1);
  if (digits.startsWith("63")) digits = digits.slice(2);
  // Now digits should be the local part (9171234567)
  if (digits.length >= 7) {
    const masked = "*".repeat(digits.length - 2);
    const last2 = digits.slice(-2);
    return `+63${masked}${last2}`;
  }
  // fallback: just show as is
  return phone;
}

const OtpModal = React.memo(
  ({
    email,
    phoneInfo,
    onClose,
    onComplete,
    onChallenge,
    onVerify,
    verifyRecaptcha,
    resetRecaptcha,
  }) => {
    const [verificationErrorMessage, setVerificationErrorMessage] = useState("");
    const [mfaStep, setMfaStep] = useState(MfaStep.Challenge);
    const [otpValue, setOtpValue] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const otpInputRef = useRef(null);

    useEffect(() => {
      if (mfaStep !== MfaStep.OtpVerification) {
        return;
      }

      const focusAnimationFrame = window.requestAnimationFrame(() => {
        if (otpInputRef.current) {
          otpInputRef.current.focus();
        }
      });

      return () => {
        window.cancelAnimationFrame(focusAnimationFrame);
      };
    }, [mfaStep]);

    const handleOtpChange = (event) => {
      const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 6);
      setOtpValue(digitsOnly);
      setVerificationErrorMessage("");
    };

    const handleMfaChallenge = async () => {
      if (isSubmitting) return;

      try {
        setVerificationErrorMessage("");
        setIsSubmitting(true);

        const recaptchaToken = await verifyRecaptcha();
        await onChallenge({ recaptchaToken });
        setMfaStep(MfaStep.OtpVerification);
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "Unable to start MFA challenge. Please try again.";
        setVerificationErrorMessage(message);
      } finally {
        resetRecaptcha();
        setIsSubmitting(false);
      }
    };

    const handleOtpVerification = async () => {
      if (isSubmitting) return;

      try {
        setVerificationErrorMessage("");
        setIsSubmitting(true);

        const recaptchaToken = await verifyRecaptcha();
        const verificationResponse = await onVerify({
          verificationCode: otpValue,
          recaptchaToken,
        });

        onComplete(verificationResponse || {});
      } catch (err) {
        const errorCode = err?.code || err?.response?.data?.code || err?.data?.code || "";
        const message =
          errorCode === "auth/invalid-verification-code"
            ? "Invalid verification code. Please check the 6-digit code and try again."
            : err?.response?.data?.message ||
              err?.data?.message ||
              err?.message ||
              "Security verification failed. Please try again.";
        setVerificationErrorMessage(message);
      } finally {
        resetRecaptcha();
        setIsSubmitting(false);
      }
    };

    return (
      <div className={styles.otpDialogOverlay} role="presentation" onClick={onClose}>
        <div
          className={styles.otpDialog}
          role="dialog"
          aria-modal="true"
          aria-labelledby="otp-dialog-title"
          aria-describedby="otp-dialog-description"
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" className={styles.closeButton} onClick={onClose}>
            x
          </button>

          <div className={styles.otpHeader}>
            <h2 id="otp-dialog-title">Verify Your Identity</h2>
            {mfaStep === MfaStep.Challenge ? (
              <p id="otp-dialog-description">
                To verify your identity, a one-time passcode will be sent out to your assigned
                mobile number {maskPhoneNumber(phoneInfo)}.
              </p>
            ) : (
              <p id="otp-dialog-description">
                A one-time passcode has been sent to {maskPhoneNumber(phoneInfo)}. To proceed, enter
                the passcode in the field below.
              </p>
            )}
          </div>

          <div className={styles.otpContent}>
            {mfaStep === MfaStep.Challenge ? (
              <div className={styles.otpMeta}>
                {/* Content already moved to description above, keep reCAPTCHA note if needed */}
                {/* <p>Invisible reCAPTCHA protects this step.</p> */}
              </div>
            ) : (
              <>
                <div className={styles.otpInputContainer}>
                  <div className={styles.otpSlotsGroup}>
                    {Array.from({ length: 6 }, (_, index) => (
                      <div
                        key={`otp-slot-${index + 1}`}
                        className={`${styles.otpSlot} ${index === Math.min(otpValue.length, 5) ? styles.otpSlotActive : ""}`}
                      >
                        {otpValue[index] || ""}
                      </div>
                    ))}
                  </div>
                  <input
                    ref={otpInputRef}
                    className={styles.otpHiddenInput}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otpValue}
                    onChange={handleOtpChange}
                  />
                </div>

                <div className={styles.otpMeta}>
                  {/* Content already moved to description above, keep reCAPTCHA note if needed */}
                  {/* <p>Invisible reCAPTCHA runs again when you verify OTP.</p> */}
                </div>
              </>
            )}

            {verificationErrorMessage && (
              <div className={styles.errorMessage}>{verificationErrorMessage}</div>
            )}

            {mfaStep === MfaStep.Challenge ? (
              <button
                type="button"
                className={styles.submitButton}
                onClick={handleMfaChallenge}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Starting challenge..." : "Proceed to OTP Send-Out"}
              </button>
            ) : (
              <button
                type="button"
                className={styles.submitButton}
                onClick={handleOtpVerification}
                disabled={isSubmitting || otpValue.length !== 6}
              >
                {isSubmitting ? "Verifying OTP..." : "Verify OTP & Continue"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  },
);

const LoginModule = () => {
  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [firebaseMfaResolver, setFirebaseMfaResolver] = useState(null);
  const [firebaseMfaHints, setFirebaseMfaHints] = useState([]);
  const [selectedMfaHintUid, setSelectedMfaHintUid] = useState("");
  const [firebaseMfaVerificationId, setFirebaseMfaVerificationId] = useState("");
  const firebaseRecaptchaVerifierRef = useRef(null);
  const firebaseRecaptchaWidgetIdRef = useRef(null);
  const firebaseAuthRef = useRef(null);

  const onSolvedRecaptcha = () => {
    // no-op: OTP errors are handled within the modal component
  };

  const getFirebaseAuthInstance = useCallback(() => {
    if (firebaseAuthRef.current) {
      return firebaseAuthRef.current;
    }

    const firebaseConfig = env.FIREBASE_CONFIG || {};
    const requiredFirebaseFields = ["apiKey", "authDomain", "projectId", "appId"];
    const missingFirebaseFields = requiredFirebaseFields.filter(
      (field) => !String(firebaseConfig?.[field] || "").trim(),
    );

    if (missingFirebaseFields.length > 0) {
      throw new Error(`Firebase configuration is incomplete: ${missingFirebaseFields.join(", ")}.`);
    }

    const firebaseApp = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    const auth = getAuth(firebaseApp);
    const tenantId =
      typeof env.FIREBASE_TENANT_ID === "string" ? env.FIREBASE_TENANT_ID.trim() : "";

    auth.tenantId = tenantId || null;
    firebaseAuthRef.current = auth;

    return auth;
  }, []);

  const clearRecaptcha = () => {
    if (firebaseRecaptchaVerifierRef.current) {
      firebaseRecaptchaVerifierRef.current.clear();
      firebaseRecaptchaVerifierRef.current = null;
    }

    const recaptchaContainer = document.getElementById("login-invisible-recaptcha");
    if (recaptchaContainer) {
      recaptchaContainer.innerHTML = "";
    }

    firebaseRecaptchaWidgetIdRef.current = null;
  };

  const ensureFirebaseRecaptchaVerifier = useCallback(async () => {
    const auth = getFirebaseAuthInstance();

    if (!firebaseRecaptchaVerifierRef.current) {
      firebaseRecaptchaVerifierRef.current = new RecaptchaVerifier(
        "login-invisible-recaptcha",
        {
          size: "invisible",
          callback: () => {
            onSolvedRecaptcha();
          },
          "expired-callback": () => {
            resetRecaptcha();
          },
        },
        auth,
      );
    }

    if (firebaseRecaptchaWidgetIdRef.current === null) {
      firebaseRecaptchaWidgetIdRef.current = await firebaseRecaptchaVerifierRef.current.render();
    }

    return firebaseRecaptchaVerifierRef.current;
  }, [getFirebaseAuthInstance]);

  const resetRecaptcha = () => {
    if (window.grecaptcha && firebaseRecaptchaWidgetIdRef.current !== null) {
      window.grecaptcha.reset(firebaseRecaptchaWidgetIdRef.current);
    }
  };

  useEffect(() => {
    if (!showOtpModal) {
      return;
    }

    ensureFirebaseRecaptchaVerifier().catch(() => {
      // Surface is handled when challenge/verify is attempted.
    });
  }, [showOtpModal, ensureFirebaseRecaptchaVerifier]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prevForm) => ({ ...prevForm, [name]: value }));
    setErrorMessage("");
  };

  useEffect(() => {
    return () => {
      clearRecaptcha();
    };
  }, []);

  const closeOtpModal = () => {
    setShowOtpModal(false);
    setFirebaseMfaResolver(null);
    setFirebaseMfaHints([]);
    setSelectedMfaHintUid("");
    setFirebaseMfaVerificationId("");
    clearRecaptcha();
  };

  const completeLogin = ({
    idToken,
    accessToken,
    AccessToken,
    token,
    refreshToken,
    RefreshToken,
  } = {}) => {
    const organizationId = "CAV1";
    const resolvedAccessToken = idToken || accessToken || AccessToken || token || "";
    const resolvedRefreshToken = refreshToken || RefreshToken || "";

    if (!resolvedAccessToken) {
      throw new Error("OTP verified but no access token was returned. Please try again.");
    }

    localStorage.setItem("accessToken", resolvedAccessToken);
    localStorage.setItem("refreshToken", resolvedRefreshToken);
    localStorage.setItem("email", form.email.trim());
    localStorage.setItem("organizationId", organizationId);
    localStorage.setItem("campaignCode", organizationId);

    redirectTo(Path.Slash);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (isLoggingIn) return;

    try {
      setErrorMessage("");
      setIsLoggingIn(true);
      const auth = getFirebaseAuthInstance();
      const userCredential = await signInWithEmailAndPassword(
        auth,
        form.email.trim(),
        form.password,
      );
      const idToken = await userCredential.user.getIdToken();
      const refreshToken = (await userCredential.user.refreshToken) || "";

      completeLogin({ idToken, refreshToken });
    } catch (err) {
      if (err?.code === "auth/multi-factor-auth-required") {
        try {
          const auth = getFirebaseAuthInstance();
          const resolver = getMultiFactorResolver(auth, err);
          const phoneHints = (resolver?.hints || []).filter(
            (hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
          );

          if (!phoneHints.length) {
            throw new Error("No enrolled phone factors found for this account.");
          }

          setFirebaseMfaResolver(resolver);
          setFirebaseMfaHints(phoneHints);
          setSelectedMfaHintUid(phoneHints[0]?.uid || "");
          setFirebaseMfaVerificationId("");
          setShowOtpModal(true);
          return;
        } catch (resolverError) {
          setErrorMessage(
            resolverError?.message ||
              "This account requires Firebase MFA, but MFA setup could not be initialized.",
          );
          return;
        }
      }

      let message =
        err?.data?.message ||
        err?.message ||
        err?.statusText ||
        "Invalid credentials. Check your Email and Password.";

      if (err?.code === "auth/invalid-login-credentials") {
        message = "The email or password you entered is incorrect. Please try again.";
      }

      setErrorMessage(message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const verifyRecaptcha = async () => {
    const recaptchaVerifier = await ensureFirebaseRecaptchaVerifier();
    const token = await recaptchaVerifier.verify();

    if (typeof token === "string" && token.trim()) {
      return token.trim();
    }

    throw new Error("Security verification failed. Please try again.");
  };

  const handleMfaChallenge = async () => {
    if (!firebaseMfaResolver) {
      throw new Error("Start sign-in first to initialize Firebase MFA resolver.");
    }

    const selectedHint = firebaseMfaHints.find((hint) => hint.uid === selectedMfaHintUid);
    if (!selectedHint) {
      throw new Error("No phone factor selected for MFA.");
    }

    await ensureFirebaseRecaptchaVerifier();

    const phoneAuthProvider = new PhoneAuthProvider(getFirebaseAuthInstance());
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      {
        multiFactorHint: selectedHint,
        session: firebaseMfaResolver.session,
      },
      firebaseRecaptchaVerifierRef.current,
    );

    setFirebaseMfaVerificationId(verificationId);
    return { verificationId };
  };

  const handleMfaVerify = async ({ verificationCode } = {}) => {
    const normalizedVerificationCode = String(verificationCode || "").trim();
    if (!normalizedVerificationCode) {
      throw new Error("MFA verification failed. verificationCode is required.");
    }

    if (!firebaseMfaResolver || !firebaseMfaVerificationId) {
      throw new Error("Start MFA challenge first to send the verification code.");
    }

    const credential = PhoneAuthProvider.credential(
      firebaseMfaVerificationId,
      normalizedVerificationCode,
    );
    const assertion = PhoneMultiFactorGenerator.assertion(credential);
    const userCredential = await firebaseMfaResolver.resolveSignIn(assertion);
    const idToken = await userCredential.user.getIdToken();
    const refreshToken = userCredential.user.refreshToken || "";

    return { idToken, refreshToken };
  };

  const resolvedPhoneInfo =
    firebaseMfaHints.find((hint) => hint.uid === selectedMfaHintUid)?.phoneNumber ||
    firebaseMfaHints[0]?.phoneNumber ||
    "+********0415";

  return (
    <div className={`${styles.page} ${showOtpModal ? styles.pageModalOpen : ""}`}>
      <div className={styles.ambient}>
        <div className={styles.orbTop} />
        <div className={styles.orbBottom} />
      </div>

      <div className={styles.loginCard}>
        <div className={styles.header}>
          <div className={styles.brandIconWrap}>
            <div className={styles.brandIcon}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className={styles.brandIconSvg}
                role="img"
                aria-label="Nexus analytics icon"
              >
                <path
                  d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <h1 className={styles.title}>Nexus Recording</h1>
          <p className={styles.subtitle}>Command Center Portal</p>
        </div>

        <form className={styles.form} onSubmit={handleLogin} aria-busy={isLoggingIn}>
          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

          <div className={styles.fieldGroup}>
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter email address"
              value={form.email}
              onChange={handleInputChange}
              disabled={isLoggingIn}
              required
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="Enter password"
              value={form.password}
              onChange={handleInputChange}
              disabled={isLoggingIn}
              required
            />
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoggingIn || !form.email.trim() || !form.password}
          >
            {isLoggingIn ? (
              <>
                <span className={styles.buttonSpinner} aria-hidden="true" />
                Signing in...
              </>
            ) : (
              "Continue"
            )}
          </button>
        </form>

        <div className={styles.demoCredentials}>Use your authentication service credentials.</div>
        <div id="login-invisible-recaptcha" />
      </div>

      {showOtpModal && (
        <OtpModal
          email={form.email}
          phoneInfo={resolvedPhoneInfo}
          onClose={closeOtpModal}
          onComplete={(verificationData) => completeLogin({ ...(verificationData || {}) })}
          onChallenge={handleMfaChallenge}
          onVerify={handleMfaVerify}
          verifyRecaptcha={verifyRecaptcha}
          resetRecaptcha={resetRecaptcha}
        />
      )}
    </div>
  );
};

export default LoginModule;
